import store from '@/store';

/**
 * Composable for managing and applying theme from API.
 * Centralizes CSS variable injection, replacing duplicated logic
 * across entry points (dashboards/index, MemberLayout, Switch, MemberDashboard).
 */
export function useTheme() {

    /**
     * Convert a hex color to a soft tinted version (mix with white)
     * @param {string} hex - Hex color (e.g. '#F93E3E')
     * @param {number} tint - Tint factor 0-1 (0 = original, 1 = white)
     * @returns {string} Hex color string
     */
    function hexToSoftTint(hex, tint = 0.88) {
        const c = hex.replace('#', '');
        const r = parseInt(c.substring(0, 2), 16);
        const g = parseInt(c.substring(2, 4), 16);
        const b = parseInt(c.substring(4, 6), 16);
        const tr = Math.round(r + (255 - r) * tint);
        const tg = Math.round(g + (255 - g) * tint);
        const tb = Math.round(b + (255 - b) * tint);
        return `#${tr.toString(16).padStart(2, '0')}${tg.toString(16).padStart(2, '0')}${tb.toString(16).padStart(2, '0')}`;
    }

    /**
     * Fetch theme data from API and inject CSS variables into :root
     * @param {string} corporateId - The corporate ID to fetch theme for
     * @returns {Promise<void>}
     */
    async function applyTheme(corporateId) {
        if (!corporateId) return;

        try {
            await store.dispatch('ThemeCorporate/read_single', corporateId);
            const themeData = store.state.ThemeCorporate;

            if (themeData.themeCorporate && Object.keys(themeData.themeCorporate).length > 0) {
                const tc = themeData.themeCorporate;
                const root = document.documentElement;

                // Existing tokens
                if (tc.top_navbar) root.style.setProperty('--top', tc.top_navbar);
                if (tc.sidebar_navbar) root.style.setProperty('--sidebar', tc.sidebar_navbar);
                if (tc.bottom_navbar) root.style.setProperty('--bottom', tc.bottom_navbar);
                if (tc.text_navbar) root.style.setProperty('--text-navbar', tc.text_navbar);

                // Always set primary colors, fallback to default if missing
                const primaryColor = tc.primary_button || '#F27178';
                root.style.setProperty('--primary-color', primaryColor);

                // Derived tokens from primary_button
                root.style.setProperty('--primary-bg', primaryColor);
                root.style.setProperty('--primary-icon', primaryColor);
                root.style.setProperty('--primary-border', primaryColor);
                root.style.setProperty('--primary-gradient',
                    `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}CC 100%)`);
                root.style.setProperty('--secondary-color', hexToSoftTint(primaryColor, 0.88));
            } else {
                applyDefaultTheme();
            }
        } catch (err) {
            console.error('Error fetching theme:', err);
            applyDefaultTheme();
        }
    }

    /**
     * Apply default fallback theme when no theme data is available
     */
    function applyDefaultTheme() {
        const root = document.documentElement;
        root.style.setProperty('--top', '#FC6666');
        root.style.setProperty('--sidebar', '#CB5B5B');
        root.style.setProperty('--bottom', '#663434');
        root.style.setProperty('--text-navbar', '#FFFFFF');
        root.style.setProperty('--primary-color', '#F27178');
        root.style.setProperty('--primary-bg', '#FC6666');
        root.style.setProperty('--primary-icon', '#F27178');
        root.style.setProperty('--primary-border', '#F27178');
        root.style.setProperty('--primary-gradient',
            'linear-gradient(135deg, #F27178 0%, #E05A61 100%)');
        root.style.setProperty('--secondary-color', '#FEE9E9');
    }

    return { applyTheme, applyDefaultTheme };
}
